import React, { useEffect } from "react";
import Btn from "../btn";
import SelectionChip from "../selection/Chip";
import SelectionDropdown from "../selection/Dropdown";
import * as Icons from "../../assets/svgs/icons";

type InputType = "text" | "dropdown" | "file" | "chip" | "multiline";

type InputFieldProps<T extends InputType = "text"> = {
  label?: string;
  required?: boolean;
  helperText?: string;
  size?: "md" | "lg";
  type: T;
  value?: T extends "chip" ? string[] : string;
  options?: string[];
  chips?: string[];
  selectedChip?: string[];
  onChange?: T extends "chip"
    ? (value: string[]) => void
    : T extends "file"
    ? (value: File) => void
    : (value: string) => void;
};

function Input<T extends InputType = "text">({
  label,
  required = false,
  helperText,
  size = "md",
  type = "text" as T,
  value,
  options = [],
  chips = [],
  selectedChip,
  onChange,
}: InputFieldProps<T>) {
  const [chipState, setChipState] = React.useState<string[]>(
    selectedChip || []
  );
  const height = size === "lg" ? "48px" : "40px";
  const textSize = size === "lg" ? "16px" : "14px";
  const requiredIconSize = size === "lg" ? 12 : 10;

  useEffect(() => {
    if (type === "chip" && onChange) {
      (onChange as (value: string[]) => void)(chipState);
    }
  }, [chipState, type, onChange]);

  return (
    <div className={`flex flex-col gap-2 w-[100%]`}>
      {label && (
        <div className="flex items-center gap-1">
          <span className="font-bold text-[#212121] text-[14px]">{label}</span>
          {required && (
            <Icons.Required
              width={requiredIconSize}
              height={requiredIconSize}
            />
          )}
        </div>
      )}
      {helperText && (
        <span className="text-[#888888] text-[12px]">{helperText}</span>
      )}
      {type === "text" && (
        <input
          className={`bg-white border border-[#eeeeee] rounded-[8px] px-3 h-[${height}] text-[${textSize}]`}
          value={value as string}
          onChange={(e) =>
            (onChange as (value: string) => void)?.(e.target.value)
          }
          placeholder="holder text"
        />
      )}
      {type === "multiline" && (
        <textarea
          className={`bg-[#f7f7f7] border-none rounded-[8px] px-3 py-2 min-h-[80px] text-[${textSize}] text-[#212121] resize-none`}
          value={value as string}
          onChange={(e) =>
            (onChange as (value: string) => void)?.(e.target.value)
          }
          placeholder="입력해주세요."
        />
      )}
      {type === "dropdown" && (
        <SelectionDropdown
          size={size}
          options={options}
          value={value as string}
          onChange={onChange as (value: string) => void}
          placeholder="holder text"
        />
      )}
      {type === "chip" && (
        <div className="flex flex-wrap gap-2">
          {chips?.map((chip, idx) => {
            const actived = chipState.includes(chip);
            return (
              <div
                key={idx}
                onClick={() => {
                  setChipState(
                    actived
                      ? chipState.filter((c) => c !== chip)
                      : [...chipState, chip]
                  );
                }}
              >
                <SelectionChip
                  size={size === "lg" ? "md" : "sm"}
                  labels={[chip]}
                  state={actived ? "actived" : "default"}
                  className="cursor-pointer"
                />
              </div>
            );
          })}
        </div>
      )}
      {type === "file" && (
        <div
          className={`flex items-center border border-[#eeeeee] rounded-[8px] pl-3 h-[${height}]`}
        >
          <span className="flex-1 text-[#888888] text-[12px]">
            {(value as string) || "파일명 또는 파일 타입"}
          </span>
          <input
            type="file"
            id="inputfile"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                (onChange as (value: File) => void)?.(file);
              }
            }}
          />
          <Btn.Secondary
            disabled={false}
            color="gray"
            type="button"
            value="파일선택"
            onClick={() => {
              const input = document.getElementById(
                "inputfile"
              ) as HTMLInputElement;
              input?.click();
            }}
          >
            파일선택
          </Btn.Secondary>
        </div>
      )}
    </div>
  );
}

export default Input;
